import React, { useEffect, useMemo, useState, useRef } from 'react';
import CustomDropdown from 'components/v2/dropdowns/CustomDropdown';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import LoadingIndicator from 'components/v2/LoadingIndicator';
import ToggleSwitchGroup from 'components/v2/ToggleSwitchGroup';
import { useTranslation } from 'react-i18next';
import FSCSettingsForm from 'components/modules/user/rates/drayage/FSCSettingsForm';
import FSCSettingsSummary from 'components/modules/user/rates/drayage/FSCSettingsSummary';
import CustomerSearch from 'components/site/customer-search';
import SearchBox from 'components/site/SearchBox';
import LocationWebAPIUtils from 'utils/LocationWebAPIUtils';
import { FSC_TYPE } from 'constants/fsc/fsc';
import { EIA_REGIONS } from 'constants/EiaRegions';
import {
  fscPeriodType,
  getFscSettings,
  getFscSettingsById,
} from 'utils/FscSettingsApiUtils';
import moment from 'moment-timezone';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import { showToast } from 'stores/ToastStore';
import SessionStore from 'stores/SessionStore';
import Alert from 'components/ui/Alert';
import {
  getDefaultLocationFields,
  getLocationFields,
  isZipCode,
} from 'components/modules/pricing/lane-rates-v2/lane-rates-v2-helpers';
import {
  ORIGIN,
  DESTINATION,
} from 'components/modules/pricing/lane-rates-v2/lane-rates-v2-constants';
import { getCustomerName } from 'helpers/users';

const Asterisk = () => <span className="text-gray-500">*</span>;

const emptyLaneRate = {
  customerId: null,
  customerType: null,
  customerName: null,
  originZone: null,
  originZoneName: null,
  originState: null,
  originCity: null,
  originZip: null,
  destinationZone: null,
  destinationZoneName: null,
  destinationState: null,
  destinationCity: null,
  destinationZip: null,
  reversible: null,
  weeklyCommitment: null,
  rate: null,
  effectiveStart: null,
  effectiveEnd: null,
  eiaRegion: null,
  fscConfigId: null,
  alreadyConfirmed: false,
  overrideCustomerFsc: false,
};

const emptyFscSetting = {
  id: undefined,
  fscType: '',
};

export default function LaneRateForm({
  isActionCreate,
  initialLaneRates,
  laneRate: laneRateProp = {},
  onChange = (laneRate) => {},
  saving = false,
  onValidate = (valid) => {},
}) {
  const { t } = useTranslation();
  const [loadingFsc, setLoadingFsc] = useState(false);
  const [currentCustomerFscSettings, setCurrentCustomerFscSettings] =
    useState(emptyFscSetting);
  const [loadedCustomFscSettings, setLoadedCustomFscSettings] =
    useState(emptyFscSetting);
  const [fscSettings, setFscSettings] = useState(emptyFscSetting);
  const [zoneList, setZoneList] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [savingFsc, setSavingFsc] = useState(false);
  const [laneRate, setLaneRate] = useState({
    ...emptyLaneRate,
    ...laneRateProp,
    overrideCustomerFsc: Boolean(laneRateProp.fscConfigId),
  });
  const [laneRatePropCached, setLaneRatePropCached] = useState(laneRateProp);
  const [searchingUserType, setSearchingUserType] = useState('customers');
  const [valid, setValid] = useState(true);
  const saveAttempted = useRef(false);

  const isUpdatingExistingLaneRate = Boolean(laneRate.id);

  const reversibleOptions = [
    {
      name: t('YES'),
      value: true,
    },
    {
      name: t('NO'),
      value: false,
    },
  ];

  useEffect(() => {
    fetchZoneList('');
  }, []);

  useEffect(() => {
    if (saveAttempted.current) validate();
    onChange(laneRate);
  }, [laneRate]);

  useEffect(() => {
    if (laneRate.customerId) {
      fetchFscSettings();
    }
  }, [laneRate.customerId]);

  useEffect(() => {
    if (laneRate.overrideCustomerFsc) {
      if (laneRatePropCached.fscConfigId) {
        setFscSettings(loadedCustomFscSettings);
        setLaneRate((state) => ({
          ...state,
          fscConfigId: loadedCustomFscSettings.id,
        }));
      } else {
        setFscSettings({
          ...currentCustomerFscSettings,
          id: undefined,
        });
      }
    }

    if (!laneRate.overrideCustomerFsc) {
      setFscSettings({
        ...currentCustomerFscSettings,
        id: undefined,
      });
      setLaneRate((state) => ({
        ...state,
        fscConfigId: null,
      }));
    }
  }, [
    laneRate.overrideCustomerFsc,
    loadedCustomFscSettings,
    currentCustomerFscSettings,
  ]);

  useEffect(() => {
    if (!saving) onValidate(valid);
  }, [valid]);

  useEffect(() => {
    if (!saving) {
      return;
    }

    if (!saveAttempted.current) saveAttempted.current = true;

    const valid = validate();

    if (!valid) {
      onValidate(false);
      return;
    }

    if (laneRate.overrideCustomerFsc) {
      setSavingFsc(true);
    } else {
      onValidate(true);
    }
  }, [saving]);

  const eiaRegions = useMemo(() => {
    return Object.keys(EIA_REGIONS).map((key) => ({
      name: t(EIA_REGIONS[key].slug),
      value: EIA_REGIONS[key].value,
    }));
  }, []);

  const eiaRegionVisible = useMemo(
    () =>
      [FSC_TYPE.VARIABLE_AMOUNT_PER_MILE, FSC_TYPE.VARIABLE_PERCENT].includes(
        fscSettings.fscType,
      ),
    [fscSettings.fscType],
  );

  useEffect(() => {
    const editableLateRate = initialLaneRates?.find(
      (initialLaneRate) => initialLaneRate?.id === laneRate?.id,
    );
    setLaneRate((state) => ({
      ...state,
      eiaRegion: eiaRegionVisible ? editableLateRate?.eiaRegion : '',
    }));
  }, [eiaRegionVisible]);

  useEffect(() => {
    const isShipperLanePricingManager =
      SessionStore.isShipperLanePricingManager();
    const isCarrierLanePricingManager =
      SessionStore.isCarrierLanePricingManager();
    let res = null;

    if (isShipperLanePricingManager) {
      res = 'shippers';
    }

    if (isCarrierLanePricingManager) {
      res = 'carriers';
    }

    if (isShipperLanePricingManager && isCarrierLanePricingManager) {
      res = null;
    }

    setSearchingUserType(res);
  }, []);

  const validate = () => {
    const formErrors = {};

    if (!laneRate.customerId) {
      formErrors.customerId = t('REQUIRED_FIELD');
    }

    if (!laneRate.originZone && !laneRate.originCity && !laneRate.originZip) {
      formErrors.originZoneName = t('REQUIRED_FIELD');
    }

    if (
      !laneRate.destinationZone &&
      !laneRate.destinationCity &&
      !laneRate.destinationZip
    ) {
      formErrors.destinationZoneName = t('REQUIRED_FIELD');
    }

    if (
      laneRate.originZip &&
      (laneRate.originZip.length < 5 || laneRate.originZip.length > 5)
    ) {
      formErrors.originZoneName = t('ZIP_CODE_MUST_BE_5_DIGITS');
    }

    if (
      laneRate.destinationZip &&
      (laneRate.destinationZip.length < 5 || laneRate.destinationZip.length > 5)
    ) {
      formErrors.destinationZoneName = t('ZIP_CODE_MUST_BE_5_DIGITS');
    }
    if (typeof laneRate.reversible !== 'boolean') {
      formErrors.reversible = t('REQUIRED_FIELD');
    }

    if (!laneRate.rate) {
      formErrors.rate = t('REQUIRED_FIELD');
    }

    if (!formErrors.rate && laneRate.rate <= 0) {
      formErrors.rate = t('RATE_MUST_BE_GREATER_THAN_0');
    }

    if (!laneRate.originState && isActionCreate) {
      // when editing an existing lane rate, state is optional
      // because 1) we have existing data that does not have have the state field populated and
      // 2) the requirements for lane rate fields will change in rate conditions epic so this is a temp workaround

      formErrors.originState = t('REQUIRED_FIELD');
    }

    if (!laneRate.destinationState && isActionCreate) {
      formErrors.destinationState = t('REQUIRED_FIELD');
    }

    if (!laneRate.effectiveStart) {
      formErrors.effectiveStart = t('REQUIRED_FIELD');
    }

    if (!laneRate.effectiveEnd) {
      formErrors.effectiveEnd = t('REQUIRED_FIELD');
    }

    if (
      laneRate.effectiveStart &&
      laneRate.effectiveEnd &&
      moment(laneRate.effectiveStart).isAfter(laneRate.effectiveEnd)
    ) {
      formErrors.effectiveEnd = t('VALIDITY_END_DATE_BEFORE_START_DATE');
    }

    if (eiaRegionVisible && !laneRate.eiaRegion) {
      formErrors.eiaRegion = t('REQUIRED_FIELD');
    }

    const valid = Object.keys(formErrors).length === 0;
    setValid(valid);
    setFormErrors(formErrors);

    return valid;
  };

  const handleCustomerChange = (customer) => {
    const customerName = getCustomerName(customer);

    setLaneRate((state) => {
      return {
        ...state,
        customerId: customer._id,
        customerName,
        customerType: customer.type,
      };
    });
  };

  const fetchFscSettings = async () => {
    setLoadingFsc(true);

    await Promise.all([
      fetchCurrentCustomerFscSettings(),
      fetchCustomFscSettings(),
    ]);

    setLoadingFsc(false);
  };

  const fetchCurrentCustomerFscSettings = async () => {
    try {
      const { rows } = await getFscSettings(
        laneRate?.customerId,
        fscPeriodType.CURRENT,
      );
      const fscSettings = rows.length > 0 ? rows[0] : {};

      setCurrentCustomerFscSettings(fscSettings);
    } catch (e) {
      showToast(t('REQUEST_ERROR'), t('ERROR_FETCHING_FSC'), 'error');
      console.error('ERROR_FETCHING_FSC', e);
    }
  };

  const fetchCustomFscSettings = async () => {
    if (!laneRatePropCached.fscConfigId) {
      return;
    }

    try {
      const fscSettings = await getFscSettingsById(
        laneRatePropCached.fscConfigId,
      );

      setLoadedCustomFscSettings(fscSettings);
    } catch (e) {
      showToast(t('REQUEST_ERROR'), t('ERROR_FETCHING_FSC'), 'error');
      console.error('ERROR_FETCHING_FSC', e);
    }
  };

  const fetchZoneList = async (zoneName) => {
    try {
      let query = '';

      if (zoneName.length) {
        let filterString = {};

        if (zoneName) {
          filterString.name = zoneName;
        }

        query = new URLSearchParams(filterString).toString();
      }

      const response = await LocationWebAPIUtils.getCustomZones(query);

      if (response.zones && response.zones.length) {
        setZoneList(response.zones);
      }
    } catch (e) {
      console.error('failed to fetch market values: ', e);
    }
  };

  const handleZoneSearch = (query) => {
    if (!parseInt(query, 10)) {
      fetchZoneList(query);
    }
  };

  const setZipData = ({ type, location }) => {
    const defaultLocations = getDefaultLocationFields(type);
    const { zipField } = getLocationFields(type);
    setLaneRate((prevLaneRate) => {
      return {
        ...prevLaneRate,
        ...defaultLocations,
        [zipField]: location,
      };
    });
  };

  const setCustomZone = ({
    type,
    location,
    customZoneField,
    customZoneNameField,
  }) => {
    const defaultLocations = getDefaultLocationFields(type);

    setLaneRate((prevLaneRate) => {
      return {
        ...prevLaneRate,
        ...defaultLocations,
        [customZoneField]: location,
        [customZoneNameField]: location.name,
      };
    });
  };

  const setStateField = ({ type, state }) => {
    const { stateField } = getLocationFields(type);
    setLaneRate((prevLaneRate) => {
      return {
        ...prevLaneRate,
        [stateField]: state,
      };
    });
  };

  const setCity = ({ type, cityField, location }) => {
    const defaultLocations = getDefaultLocationFields(type);
    return setLaneRate((prevLaneRate) => {
      return {
        ...prevLaneRate,
        ...defaultLocations,
        [cityField]: location,
      };
    });
  };

  const handleCityZipZoneChange = ({ type, location }) => {
    const { customZoneField, customZoneNameField, cityField } =
      getLocationFields(type);

    if (isZipCode(location)) {
      return setZipData({ type, location });
    }

    if (typeof location === 'object') {
      return setCustomZone({
        type,
        location,
        customZoneField,
        customZoneNameField,
      });
    }

    if (typeof location === 'string') {
      return setCity({ type, location, cityField });
    }
  };

  const renderZoneItem = (item) => {
    return <div key={item.id}>{item.name}</div>;
  };

  const handleChangeOverrideCustomerFsc = (checked) => {
    setLaneRate((state) => ({
      ...state,
      overrideCustomerFsc: checked,
      fscConfigId: checked ? laneRatePropCached.fscConfigId : null,
    }));
  };

  const handleFscSaveSuccess = (data) => {
    setSavingFsc(false);
    setFscSettings(data);

    setLaneRate((state) => ({
      ...state,
      fscConfigId: data.id,
    }));

    onValidate(true);
  };

  const handleFscSaveFail = () => {
    setSavingFsc(false);
    onValidate(false);
  };

  const renderZone = (type) => {
    let inputValue =
      laneRate.originZoneName || laneRate.originCity || laneRate.originZip;

    if (type === DESTINATION) {
      inputValue =
        laneRate.destinationZoneName ||
        laneRate.destinationCity ||
        laneRate.destinationZip;
    }

    if (isUpdatingExistingLaneRate) {
      return <TextField value={inputValue} disabled />;
    }

    return (
      <SearchBox
        items={zoneList || []}
        value={inputValue}
        search={(text) => handleZoneSearch(text)}
        placeholder={t('ZONE_CITY_ZIP')}
        clearOnSelect
        onChange={(item) => handleCityZipZoneChange({ type, location: item })}
        onSelect={(value, item) =>
          handleCityZipZoneChange({ type, location: item })
        }
        disabled={isUpdatingExistingLaneRate}
        renderItem={renderZoneItem}
        testDataId={`lane-rate-zone-${type}-search`}
      />
    );
  };

  const renderErrorMessage = (error) =>
    error && <ErrorLabel className="mt-1">{error}</ErrorLabel>;

  const renderCustomerFSCFields = () => {
    if (!laneRate.customerId) {
      return null;
    }

    if (loadingFsc) {
      return (
        <div className="flex items-center space-x-1 text-sm">
          <LoadingIndicator />
          <span>{t('LOADING')}</span>
        </div>
      );
    }

    if (!fscSettings) {
      return null;
    }

    return (
      <>
        {eiaRegionVisible && (
          <div>
            <Label className="mb-1">
              {t('EIA_REGION')}
              <Asterisk />
            </Label>
            <CustomDropdown
              options={eiaRegions}
              placement="bottom-start"
              value={eiaRegions.find(
                (region) => region.value === laneRate.eiaRegion,
              )}
              onChange={(region) =>
                setLaneRate((state) => ({
                  ...state,
                  eiaRegion: region.value,
                }))
              }
              testDataId="lane-rate-eia-region-drop-down"
            />
            {renderErrorMessage(formErrors.eiaRegion)}
          </div>
        )}
        <div className="my-4 col-span-full">
          <div className="mb-4">
            <h3 className="mb-2">
              {t('CURRENT_CUSTOMER_ACCOUNT_FSC_SETTINGS')}
            </h3>
            <div
              className="flex items-center space-x-2"
              testDataId="lane-rate-current-customer-account-toggle"
            >
              <ToggleSwitchGroup
                enabled={laneRate.overrideCustomerFsc}
                onChange={handleChangeOverrideCustomerFsc}
              >
                {t('SET_CUSTOM_FSC_SETTINGS_FOR_THIS_LANE')}
              </ToggleSwitchGroup>
            </div>
          </div>

          {laneRate.overrideCustomerFsc && (
            <FSCSettingsForm
              accountId={laneRate.customerId}
              data={fscSettings}
              saving={savingFsc}
              onChange={setFscSettings}
              onSaveSuccess={handleFscSaveSuccess}
              onSaveFail={handleFscSaveFail}
              customMode
            />
          )}
          {!laneRate.overrideCustomerFsc &&
            (fscSettings.fscType ? (
              <FSCSettingsSummary
                data={fscSettings}
                accountId={laneRate.customerId}
              />
            ) : (
              <Alert type="warning" title={t('FSC_NOT_APPLIED')}>
                {t('FSC_NOT_APPLIED_DESCRIPTION')}
              </Alert>
            ))}
        </div>
      </>
    );
  };

  return (
    <div>
      <p className="text-gray-500 text-xs mb-4">
        <span className="font-medium">{t('REQUIRED_FIELD_INDICATOR')}</span>
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 mb-4 gap-4">
        <div
          className="mb-4 col-span-full sm:col-span-1"
          testDataId="lanerate-shipper-carrier"
        >
          <Label className="mb-1">
            {t('CARRIER')}/{t('SHIPPER')}
            <Asterisk />
          </Label>
          {isUpdatingExistingLaneRate && (
            <TextField
              name="shipper/carrier"
              value={laneRate.customerName}
              disabled={isUpdatingExistingLaneRate}
            />
          )}
          {!isUpdatingExistingLaneRate && (
            <CustomerSearch
              clearOnSelect={false}
              type={searchingUserType}
              onSelect={(item, value) => handleCustomerChange(value)}
              onChange={(item, value) => handleCustomerChange(value)}
              value={laneRate.customerName}
              selected={laneRate.customerName}
              placeholder={t('SEARCH_FOR_CUSTOMER')}
              dropdownStyle={{ maxHeight: '160px', overflow: 'auto' }}
              testDataId="lane-rate-customer-search"
            />
          )}
          {renderErrorMessage(formErrors.customerId)}
        </div>
        <div className="col-span-full sm:col-span-2 sm:grid sm:grid-cols-2 sm:gap-4">
          <div
            className="col-span-full sm:col-span-1 mb-4"
            testDataId="lanerate-origin-zone"
          >
            <Label className="mb-1">
              {t('ORIGIN_ZONE')}
              <Asterisk />
            </Label>
            {renderZone(ORIGIN)}
            {renderErrorMessage(formErrors.originZoneName)}
          </div>
          <div className="col-span-full sm:col-span-1">
            <Label className="mb-1">
              {t('ORIGIN_STATE')}
              {isActionCreate && <Asterisk />}
            </Label>

            <TextField
              name="origin-state"
              value={laneRate.originState}
              onChange={(value) =>
                setStateField({ state: value, type: ORIGIN })
              }
              disabled={isUpdatingExistingLaneRate}
            />
            {renderErrorMessage(formErrors.originState)}
          </div>
        </div>
        <div className="col-span-full sm:col-span-2 sm:grid sm:grid-cols-2 sm:gap-4">
          <div className="mb-4 col-span-full sm:col-span-1">
            <Label className="mb-1">
              {t('DESTINATION_ZONE')}
              <Asterisk />
            </Label>
            {renderZone(DESTINATION)}
            {renderErrorMessage(formErrors.destinationZoneName)}
          </div>
          <div className="mb-4 col-span-full sm:col-span-1">
            <Label className="mb-1">
              {t('DESTINATION_STATE')}
              {isActionCreate && <Asterisk />}
            </Label>

            <TextField
              name="destination-state"
              value={laneRate.destinationState}
              onChange={(value) =>
                setStateField({ state: value, type: DESTINATION })
              }
              disabled={isUpdatingExistingLaneRate}
            />
            {renderErrorMessage(formErrors.destinationState)}
          </div>
        </div>
        <div className="mb-4">
          <Label className="mb-1">
            {t('REVERSIBLE')}
            <Asterisk />
          </Label>
          <CustomDropdown
            options={reversibleOptions}
            value={reversibleOptions.find(
              (option) => option.value === laneRate.reversible,
            )}
            onChange={(option) =>
              setLaneRate((state) => ({ ...state, reversible: option.value }))
            }
            testDataId="lane-rate-reversible-drop-down"
          />
          {renderErrorMessage(formErrors.reversible)}
        </div>
        <div className="mb-4">
          <Label className="mb-1">{t('WEEKLY_COMMITMENT')}</Label>
          <TextField
            type="number"
            name="weekly-commitment"
            value={laneRate.weeklyCommitment}
            onChange={(value) =>
              setLaneRate((state) => ({
                ...state,
                weeklyCommitment: value,
              }))
            }
          />
        </div>
        <div>
          <Label htmlFor="rate">
            {t('RATE', { count: 1 })}
            <Asterisk />
          </Label>
          <TextField
            className="mt-1"
            name="rate"
            id="rate"
            type="number"
            value={laneRate.rate}
            onChange={(value) => {
              setLaneRate((state) => ({ ...state, rate: value }));
            }}
          />
          {renderErrorMessage(formErrors.rate)}
        </div>
        <div>
          <Label htmlFor="validityStartDate">
            {t('VALIDITY_START_DATE')}
            <Asterisk />
          </Label>
          <TextField
            className="mt-1"
            id="validityStartDate"
            name="validity-start-date"
            type="date"
            value={moment(laneRate.effectiveStart).utc().format('YYYY-MM-DD')}
            onChange={(value) => {
              setLaneRate((state) => ({
                ...state,
                effectiveStart: value,
              }));
            }}
          />
          {renderErrorMessage(formErrors.effectiveStart)}
        </div>
        <div>
          <Label htmlFor="validityEndDate">
            {t('VALIDITY_END_DATE')}
            <Asterisk />
          </Label>
          <TextField
            className="mt-1"
            id="validityEndDate"
            name="validity-end-date"
            type="date"
            value={moment(laneRate.effectiveEnd).utc().format('YYYY-MM-DD')}
            onChange={(value) =>
              setLaneRate((state) => ({
                ...state,
                effectiveEnd: value,
              }))
            }
          />
          {renderErrorMessage(formErrors.effectiveEnd)}
        </div>

        {renderCustomerFSCFields()}
      </div>
    </div>
  );
}
